import type { Ref } from "vue";
import { HttpClient } from "@/services/httpClient";
import { Controllers, FrappeMethodType } from "@/type/api";
import type { ServiceRoom, ServiceRoomPayload } from "@/type/healthcare";
import {
  fetchFrappeDocCount,
  normalizeFrappeListResult,
  withFrappeLoading,
  type FrappeListResult,
} from "@/services/frappe/list";
import {
  buildFrappeLikeSearchFilters,
  type FrappeFilterClause,
  serializeFrappeFilters,
} from "@/services/frappe/filters";
import { HEALTHCARE_SERVICE_ROOMS_USE_MOCKS } from "@/modules/healthcare/config";

const STORAGE_KEY = "alkokh-healthcare-service-rooms-v1";
const serviceRoomService = new HttpClient(Controllers.serviceRoom);

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function seedServiceRooms(): ServiceRoom[] {
  return [
    {
      id: "ROOM-001",
      roomCode: "A-01",
      roomName: "Cat Suite 1",
      roomType: "Suite",
      image: null,
      status: "Active",
      notes: "Quiet room close to the nursing station.",
    },
    {
      id: "ROOM-002",
      roomCode: "A-02",
      roomName: "Cat Suite 2",
      roomType: "Suite",
      image: null,
      status: "Active",
      notes: "Best for shy cats and overnight observation.",
    },
    {
      id: "ROOM-003",
      roomCode: "B-01",
      roomName: "Dog Kennel 1",
      roomType: "Kennel",
      image: null,
      status: "Active",
      notes: "Medium dog room with direct walk access.",
    },
    {
      id: "ROOM-004",
      roomCode: "B-02",
      roomName: "Dog Kennel 2",
      roomType: "Kennel",
      image: null,
      status: "Active",
      notes: "Reserved for planned arrivals and active dogs.",
    },
    {
      id: "ROOM-005",
      roomCode: "R-01",
      roomName: "Observation Room 1",
      roomType: "Observation",
      image: null,
      status: "Active",
      notes: "For short-stay monitoring and medication checks.",
    },
    {
      id: "ROOM-006",
      roomCode: "R-02",
      roomName: "Observation Room 2",
      roomType: "Observation",
      image: null,
      status: "Active",
      notes: "Flexible room for rabbits and small pets.",
    },
  ];
}

function canUseWindow() {
  return typeof window !== "undefined";
}

function loadMockServiceRooms(): ServiceRoom[] {
  if (!canUseWindow())
    return seedServiceRooms();

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    const seeded = seedServiceRooms();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }

  try {
    return JSON.parse(raw) as ServiceRoom[];
  } catch {
    const seeded = seedServiceRooms();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }
}

function saveMockServiceRooms(rooms: ServiceRoom[]) {
  if (!canUseWindow())
    return;

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rooms));
}

function buildRoomId(existing: ServiceRoom[]) {
  const max = existing.reduce((currentMax, room) => {
    const numeric = Number(String(room.id || "").replace(/\D+/g, ""));
    return Number.isFinite(numeric) ? Math.max(currentMax, numeric) : currentMax;
  }, 0);

  return `ROOM-${String(max + 1).padStart(3, "0")}`;
}

function mapServiceRoom(raw: any): ServiceRoom {
  return {
    id: String(raw?.name ?? raw?.id ?? ""),
    roomCode: String(raw?.room_code ?? raw?.roomCode ?? ""),
    roomName: String(raw?.room_name ?? raw?.roomName ?? ""),
    roomType: raw?.room_type ?? raw?.roomType ?? null,
    image: raw?.image ?? null,
    status: raw?.status ?? "Active",
    notes: raw?.notes ?? raw?.note ?? null,
  };
}

export function getMockServiceRoomsSnapshot() {
  return clone(loadMockServiceRooms());
}

export async function GetServiceRooms(
  loading: Ref<boolean>,
  filter: {
    search?: string;
    status?: string | null;
    limit_start?: number;
    limit_page_length?: number;
  } = {},
): Promise<FrappeListResult<ServiceRoom>> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_SERVICE_ROOMS_USE_MOCKS) {
      const normalizedSearch = filter.search?.trim().toLowerCase() || "";
      const filtered = loadMockServiceRooms().filter((room) => {
        const matchesSearch =
          !normalizedSearch ||
          [room.roomCode, room.roomName, room.roomType, room.notes]
            .filter(Boolean)
            .some(value => String(value).toLowerCase().includes(normalizedSearch));

        const matchesStatus =
          !filter.status || String(room.status || "").toLowerCase() === String(filter.status).toLowerCase();

        return matchesSearch && matchesStatus;
      });

      const start = filter.limit_start ?? 0;
      const pageSize = filter.limit_page_length ?? filtered.length;

      return {
        data: clone(filtered.slice(start, start + pageSize)),
        total: filtered.length,
      };
    }

    const filters: FrappeFilterClause[] = [
      ...buildFrappeLikeSearchFilters(["room_name", "room_code"], filter.search),
      ...(filter.status
        ? [["Service Room", "status", "=", filter.status] as FrappeFilterClause]
        : []),
    ];

    const serializedFilters = serializeFrappeFilters(filters);

    const response = await serviceRoomService.get<{ data: any[] }>(
      "",
      {
        limit_start: filter.limit_start ?? 0,
        limit_page_length: filter.limit_page_length ?? 20,
        filters: serializedFilters,
      },
      {},
      JSON.stringify([
        "name",
        "room_code",
        "room_name",
        "room_type",
        "image",
        "status",
        "notes",
      ]),
      {},
    );

    const total = await fetchFrappeDocCount(
      serviceRoomService,
      "Service Room",
      serializedFilters,
    ).catch(() => response?.data?.length ?? 0);

    const normalized = normalizeFrappeListResult(response, total);

    return {
      data: normalized.data.map(mapServiceRoom),
      total: normalized.total,
    };
  });
}

/**
 * Fetch ONE room by its record id.
 *
 * Needed because `GetServiceRooms`' `search` builds LIKE clauses over
 * `room_name` + `room_code` only, and Frappe ANDs multiple clauses — so a record
 * id (the `name` field) can never be found through that path. Returns null when
 * the id does not exist, so callers can fall back to a code search.
 */
export async function GetServiceRoomById(
  loading: Ref<boolean>,
  roomId: string,
): Promise<ServiceRoom | null> {
  return withFrappeLoading(loading, async () => {
    const id = String(roomId ?? "").trim();
    if (!id)
      return null;

    if (HEALTHCARE_SERVICE_ROOMS_USE_MOCKS)
      return loadMockServiceRooms().find(room => room.id === id) ?? null;

    try {
      // `frappe.client.get` rather than a resource path: the resource URL is
      // built as `/api/resource/{controller}{url}`, so a bare id would produce
      // a malformed path. This form is also what the other single-doc fetches use.
      const response = await serviceRoomService.get<any>(
        "frappe.client.get",
        {},
        { doctype: "Service Room", name: id },
        undefined,
        {},
        FrappeMethodType.method,
      );

      const raw = response?.data ?? response?.message ?? response;

      return raw && (raw.name || raw.room_code) ? mapServiceRoom(raw) : null;
    }
    catch {
      // A missing room is an expected outcome of scanning an unknown code.
      return null;
    }
  });
}

export async function CreateServiceRoom(
  loading: Ref<boolean>,
  payload: ServiceRoomPayload,
): Promise<ServiceRoom | null> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_SERVICE_ROOMS_USE_MOCKS) {
      const rooms = loadMockServiceRooms();
      const created: ServiceRoom = {
        id: buildRoomId(rooms),
        roomCode: payload.roomCode.trim(),
        roomName: payload.roomName.trim(),
        roomType: payload.roomType ?? null,
        image: payload.image ?? null,
        status: payload.status ?? "Active",
        notes: payload.notes ?? null,
      };

      rooms.unshift(created);
      saveMockServiceRooms(rooms);
      return clone(created);
    }

    const response = await serviceRoomService.post<{ data?: any }>(
      {
        room_code: payload.roomCode,
        room_name: payload.roomName,
        room_type: payload.roomType,
        image: payload.image,
        status: payload.status ?? "Active",
        notes: payload.notes,
      },
    );

    return mapServiceRoom(response?.data ?? response);
  });
}

export async function UpdateServiceRoom(
  loading: Ref<boolean>,
  roomId: string,
  payload: ServiceRoomPayload,
): Promise<ServiceRoom | null> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_SERVICE_ROOMS_USE_MOCKS) {
      const rooms = loadMockServiceRooms();
      const room = rooms.find(item => item.id === roomId);

      if (!room)
        return null;

      Object.assign(room, {
        roomCode: payload.roomCode.trim(),
        roomName: payload.roomName.trim(),
        roomType: payload.roomType ?? null,
        image: payload.image ?? null,
        status: payload.status ?? "Active",
        notes: payload.notes ?? null,
      });

      saveMockServiceRooms(rooms);
      return clone(room);
    }

    const response = await serviceRoomService.put<any>(
      "",
      roomId,
      {
        room_code: payload.roomCode,
        room_name: payload.roomName,
        room_type: payload.roomType,
        image: payload.image,
        status: payload.status ?? "Active",
        notes: payload.notes,
      },
      {},
      {},
    );

    return mapServiceRoom(response?.data ?? response);
  });
}

export async function DeleteServiceRoom(
  loading: Ref<boolean>,
  roomId: string,
): Promise<boolean> {
  return withFrappeLoading(loading, async () => {
    if (HEALTHCARE_SERVICE_ROOMS_USE_MOCKS) {
      const rooms = loadMockServiceRooms();
      const filtered = rooms.filter(room => room.id !== roomId);
      saveMockServiceRooms(filtered);
      return filtered.length !== rooms.length;
    }

    await serviceRoomService.delete(roomId);
    return true;
  });
}
